/**
 * Help & Support Screen - MOBILE ONLY
 * 
 * FAQ and contact support.
 * Stacked flow - back button, no bottom tabs.
 */

import React, { useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, Pressable, Linking } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { color, text, space, touch, icon, layout } from '../../../shared/tokens/mobile';
import { ScreenContainer, SectionHeader, ListCard, ListRow } from '../../../components/v2/mobile';

export default function HelpScreen() {
  const router = useRouter();

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  const handleEmail = useCallback(() => {
    Linking.openURL('mailto:support@farmerjohn.app');
  }, []);

  return (
    <ScreenContainer>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* HEADER */}
        <View style={styles.header}>
          <Pressable onPress={handleBack} style={styles.backButton}>
            <Ionicons name="chevron-back" size={icon.lg} color={color.textPrimary} />
          </Pressable>
          <Text style={styles.title}>Help & Support</Text>
          <View style={styles.spacer} />
        </View>

        {/* CONTACT */}
        <View style={styles.section}>
          <SectionHeader label="Contact Us" />
          <ListCard>
            <ListRow
              iconName="mail-outline"
              title="Email Support"
              subtitle="support@farmerjohn.app"
              onPress={handleEmail}
              hideBorder
            />
          </ListCard>
        </View>

        {/* FAQ */}
        <View style={styles.section}>
          <SectionHeader label="Frequently Asked Questions" />
          <ListCard>
            <FAQItem
              question="How do I add a new opportunity?"
              answer="Navigate to your portfolio and tap the '+' button to add a new opportunity. You can enter details like listing URL, asking price, and location."
            />
            <FAQItem
              question="What's the difference between an opportunity and a parcel?"
              answer="An opportunity is a potential land purchase you're evaluating. It can include one or more parcels (individual plots of land with their own PIN)."
            />
            <FAQItem
              question="How does due diligence tracking work?"
              answer="Each opportunity has a due diligence checklist with customizable modules. Track progress, assign tasks to partners, and set reminders."
            />
            <FAQItem
              question="How do Ideas and Voting work?"
              answer="Partners can submit ideas for each opportunity and vote on them. This helps the team decide on land use, development plans, and next steps."
            />
            <FAQItem
              question="Can I share opportunities with my team?"
              answer="Yes! Invite partners to your portfolio and they'll have access to all opportunities, documents, and discussions within it."
              hideBorder
            />
          </ListCard>
        </View>

        {/* APP INFO */}
        <View style={styles.infoContainer}>
          <Text style={styles.infoText}>
            Need more help? Contact our support team and we'll get back to you within 24 hours.
          </Text>
        </View>
      </ScrollView>
    </ScreenContainer>
  );
}

// =============================================================================
// FAQ ITEM COMPONENT
// =============================================================================

interface FAQItemProps {
  question: string;
  answer: string;
  hideBorder?: boolean;
}

function FAQItem({ question, answer, hideBorder }: FAQItemProps) {
  return (
    <View style={[styles.faqItem, !hideBorder && styles.faqItemBorder]}>
      <Text style={styles.faqQuestion}>{question}</Text>
      <Text style={styles.faqAnswer}>{answer}</Text>
    </View>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: layout.scrollBottomPadding,
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: space.sm,
    paddingVertical: space.sm,
  },
  backButton: {
    width: touch.minimum,
    height: touch.minimum,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    flex: 1,
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
    textAlign: 'center',
  },
  spacer: {
    width: touch.minimum,
  },

  // Sections
  section: {
    paddingHorizontal: space.md,
    marginBottom: space.lg,
  },

  // FAQ item
  faqItem: {
    paddingVertical: space.md,
    paddingHorizontal: space.md,
  },
  faqItemBorder: {
    borderBottomWidth: 1,
    borderBottomColor: color.divider,
  },
  faqQuestion: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
    marginBottom: space.xs,
  },
  faqAnswer: {
    fontSize: text.body.fontSize,
    fontWeight: text.body.fontWeight,
    lineHeight: text.body.lineHeight,
    color: color.textSecondary,
  },

  // Info
  infoContainer: {
    paddingHorizontal: space.lg,
    paddingVertical: space.md,
  },
  infoText: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    color: color.textMuted,
    textAlign: 'center',
    lineHeight: text.body.lineHeight,
  },
});

